import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MapPin, Wrench, ShieldCheck, AlertCircle, ExternalLink, Search } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import CiteThisPage from "../components/CiteThisPage";
import { usePageSEO } from "../hooks/usePageSEO";
import { STATE_LOCATIONS } from "../data/stateLocations";

const BASE = "https://www.peptidesformedspas.org";

/**
 * Free interactive tool: pick a state → get instant compliance summary card.
 * Zero API calls (uses the STATE_LOCATIONS data we already ship). Designed
 * as a linkable asset — journalists, med spa consultants, and directory sites
 * link to interactive tools far more than to plain articles.
 */
const StateRegulatoryCheckerPage = () => {
  const [selected, setSelected] = useState("");
  const [query, setQuery] = useState("");

  usePageSEO(
    "State Regulatory Checker | Free Med Spa Peptide Tool",
    "Pick your state — get the medical board, delegation framework, and med spa peptide regulatory context in one click. All 50 states + DC. Not legal advice."
  );

  const states = useMemo(() => Object.values(STATE_LOCATIONS), []);
  const filtered = useMemo(() => {
    if (!query) return states;
    const q = query.toLowerCase();
    return states.filter(
      (s) => s.name.toLowerCase().includes(q) || s.abbrev.toLowerCase().includes(q)
    );
  }, [states, query]);

  const data = selected ? STATE_LOCATIONS[selected] : null;
  const pageUrl = `${BASE}/tools/state-regulatory-checker`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "@id": `${pageUrl}#app`,
      name: "State Regulatory Checker for Med Spa Peptides",
      url: pageUrl,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Any (web)",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      description:
        "Interactive tool that returns state-specific medical-practice, delegation, and med spa regulatory context for peptide research education, covering all 50 U.S. states + D.C.",
      isAccessibleForFree: true,
      inLanguage: "en-US",
      publisher: { "@id": `${BASE}/#organization` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${BASE}/tools` },
        { "@type": "ListItem", position: 3, name: "State Regulatory Checker", item: pageUrl },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid="state-checker-page">
      <Header />
      <JsonLd id="state-checker-jsonld" data={jsonLd} />

      <section className="pt-32 pb-10 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#a09186] mb-6">
            <Link to="/" className="hover:text-[#b89a6d]">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-[#3a2a26] font-medium">State Regulatory Checker</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
            <Wrench className="w-4 h-4 text-[#b89a6d]" />
            <span className="text-sm text-[#b89a6d] font-medium">Free Interactive Tool</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3a2a26] mb-6" data-testid="checker-h1">
            State Regulatory Checker
          </h1>
          <p className="text-base md:text-lg text-[#5a4a40] max-w-3xl leading-relaxed">
            Pick your state — get the medical board, delegation framework, and state-specific med spa peptide regulatory context in one click. Covers all 50 U.S. states + District of Columbia. Free to use; free to cite. Educational summary, not legal advice.
          </p>
        </div>
      </section>

      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <label htmlFor="state-search" className="block text-sm font-semibold text-[#3a2a26] mb-2">
            Search or pick a state
          </label>
          <div className="relative mb-4">
            <Search className="w-4 h-4 text-[#b89a6d] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              id="state-search"
              type="text"
              placeholder="Type a state name or abbrev (e.g., 'california' or 'ca')..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="state-checker-search"
              className="w-full pl-10 pr-4 py-3 border border-[#f0d8d4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4a5ad] focus:border-[#d4a5ad] text-[#3a2a26]"
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-72 overflow-y-auto p-2 border border-[#f0d8d4] rounded-lg bg-[#fffaf5]" data-testid="state-checker-grid">
            {filtered.map((s) => (
              <button
                key={s.slug}
                onClick={() => setSelected(s.slug)}
                data-testid={`checker-btn-${s.slug}`}
                className={`text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  selected === s.slug
                    ? "bg-[#d4a5ad] text-white font-semibold"
                    : "bg-white border border-[#f0d8d4] hover:border-[#d4a5ad] text-[#5a4a40]"
                }`}
              >
                <span className="font-semibold">{s.abbrev}</span> · {s.name}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="col-span-full text-sm text-[#a09186] italic py-4 text-center">
                No state matches "{query}".
              </p>
            )}
          </div>
        </div>
      </section>

      {data && (
        <section className="py-8 bg-[#fffaf5]" data-testid="checker-result">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white border-2 border-[#e8c5b8] rounded-2xl p-7">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-6 h-6 text-[#b89a6d]" />
                <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26]">
                  {data.name} <span className="text-[#a09186] text-base font-normal">({data.abbrev})</span>
                </h2>
              </div>

              <article className="mb-6" data-testid="checker-result-board">
                <h3 className="text-sm font-bold text-[#b89a6d] uppercase tracking-wider mb-2 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> Medical Board
                </h3>
                <p className="text-base text-[#3a2a26] font-semibold">{data.medicalBoard}</p>
              </article>

              <article className="mb-6" data-testid="checker-result-rules">
                <h3 className="text-sm font-bold text-[#b89a6d] uppercase tracking-wider mb-2">Delegation & Supervisory Framework</h3>
                <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">{data.supervisoryRule}</p>
              </article>

              <article className="mb-6" data-testid="checker-result-specific">
                <h3 className="text-sm font-bold text-[#b89a6d] uppercase tracking-wider mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> {data.name}-specific considerations
                </h3>
                <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">{data.stateSpecific}</p>
              </article>

              <div className="flex flex-wrap gap-3 pt-4 border-t border-[#f0d8d4]">
                <Link
                  to={`/locations/${data.slug}`}
                  data-testid="checker-full-page-link"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#d4a5ad] hover:bg-[#c4949c] text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  Full {data.name} guide <ExternalLink className="w-4 h-4" />
                </Link>
                <Link
                  to="/for-med-spas"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#d4a5ad] text-[#b89a6d] text-sm font-semibold rounded-lg hover:bg-[#fdf2f4] transition-colors"
                >
                  For Med Spa Owners
                </Link>
                <Link
                  to="/compliance"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#e8c5b8] text-[#5a4a40] text-sm font-semibold rounded-lg hover:border-[#d4a5ad] transition-colors"
                >
                  Compliance Framework
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-8 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <CiteThisPage
            title="State Regulatory Checker for Med Spa Peptides — PeptidesForMedSpas"
            url={pageUrl}
            publishedDate="2026-05-26"
          />
        </div>
      </section>

      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-6 text-center">
            <p className="text-sm text-[#5a4a40] leading-relaxed">
              <strong className="text-[#b89a6d]">Free to use, free to cite.</strong> This tool summarizes state regulatory context at a high level; it is not legal advice. Rules change. Always confirm with the state medical board and your own counsel before making compliance decisions.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StateRegulatoryCheckerPage;
